import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  apiRootUrl,
  helpPagesUrl,
  newsCenterUrl,
  newsPagesUrl,
} from 'common/utils/network/endpoints';
import {
  ActivePagesList,
  FAQContentType,
  GlossaryContentType,
  HomeDetails,
  NewsHomeDetails,
} from './types';

export const helpPagesApi = createApi({
  reducerPath: 'helpPages',
  baseQuery: fetchBaseQuery({
    baseUrl: apiRootUrl,
    prepareHeaders: (headers, { getState }) => {
      const { token } = (getState() as any).user
      headers.set('authorization', `Bearer ${token}`)
      return headers
    },
  }),
  tagTypes: ['HomeData', 'ArticlesList'],
  endpoints: (builder) => ({
    getHomeData: builder.query<HomeDetails, void>({
      query: () => ({
        url: `${helpPagesUrl}/home-page`,
        method: 'GET',
      }),
      transformResponse: (response: { data: HomeDetails }) => response?.data,
    }),
    getNewsHomeData: builder.query<NewsHomeDetails, any>({
      query: (clientName) => ({
        url: `${newsCenterUrl}/clients/${clientName}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: NewsHomeDetails }) => response?.data,
    }),
    getAllActivePages: builder.query<ActivePagesList, any>({
      query: (clientName) => ({
        url: `${newsCenterUrl}/clients/${clientName}/pages`,
        method: 'GET',
      }),
      transformResponse: (response: { data: ActivePagesList }) => response?.data,
    }),
    getGlossary: builder.query<any, any>({
      query: ({ clientName, clientId }) => ({
        url: `${newsCenterUrl}/clients/${clientName}/pages/${clientId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: GlossaryContentType }) => response.data,
    }),
    getFAQ: builder.query<any, any>({
      query: ({ clientName, clientId }) => ({
        url: `${newsCenterUrl}/clients/${clientName}/pages/${clientId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: FAQContentType }) => response.data,
    }),
    getArticleListData: builder.query<any, any>({
      query: ({ clientName, page, perPage }) => ({
        url: `${newsCenterUrl}/clients/${clientName}/articles`,
        method: 'GET',
        params: { page, per_page: perPage },
      }),
      providesTags: ['ArticlesList'],
      transformResponse: (response: { data: any }) => response.data,
    }),
    getArticleDetails: builder.query<any, void>({
      query: () => ({
        url: `${helpPagesUrl}/${'essential-tips-and-tricks-for-making-the-most-of-your-retirement-funds-hcx8fb6ejw'}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    getAllArticleListData: builder.query<any, any>({
      query: ({ clientName, articleCategoriesId }) => ({
        url: `${newsPagesUrl}/${clientName}/articles/${articleCategoriesId}`,
        method: 'GET',
      }),
      providesTags: ['ArticlesList'],
      transformResponse: (response: any) => ({
        data: response.data,
        perPage: response.data.articles.per_page,
        total: response.data.articles.total,
        page: response.data.articles.current_page,
      }),
    }),
    getArticleDetailsPage: builder.query<any, any>({
      query: ({ clientName, articleId }) => ({
        url: `${newsCenterUrl}/clients/${clientName}/articles/${articleId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    getDocumentsList: builder.query<any, any>({
      query: ({ clientName, clientId }) => ({
        url: `${newsCenterUrl}/clients/${clientName}/pages/${clientId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    getHelpAndSupportData: builder.query<any, any>({
      query: ({ clientName, clientId }) => ({
        url: `${newsCenterUrl}/clients/${clientName}/pages/${clientId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: any }) => response?.data,
    }),
  }),
})

export const {
  useGetHomeDataQuery,
  useGetNewsHomeDataQuery,
  useGetAllActivePagesQuery,
  useGetFAQQuery,
  useGetArticleListDataQuery,
  useGetArticleDetailsQuery,
  useGetAllArticleListDataQuery,
  useGetArticleDetailsPageQuery,
  useGetDocumentsListQuery,
  useGetGlossaryQuery,
  useGetHelpAndSupportDataQuery,
} = helpPagesApi;

export default helpPagesApi;
